// AdminPanel (Task 13.5).
//
// Administrative dashboard for Admins and Super Admins: verification queue,
// donation moderation, analytics, and report review. Admin-account management
// is shown only to a Super Admin (Req 23.12); an Admin attempting it is denied
// (Req 23.13). Non-Administrators are denied access entirely with an
// authorization error (Req 23.2) — the gate mirrors the server-authoritative
// `authorize` decision (the backend remains the real enforcer). The layout is
// card-based and fluid so it is operable from 320px to 1920px (Req 23.1).

import { useState } from "react";
import {
  authorize,
  type Report,
  type Role,
  type VerificationStatus,
} from "@sharebite/domain";
import { AsyncSection } from "../../components/AsyncSection";
import { DonationRow } from "../../components/DonationCard";
import { Card, Stat } from "../../components/primitives";
import * as api from "../../lib/api";
import type { Donation } from "@sharebite/domain";

interface PendingUser {
  uid: string;
  role: Role;
  email: string;
}

export function AdminPanel({
  session,
}: {
  session: { uid: string; role: Role | null; verificationStatus: VerificationStatus };
}): JSX.Element {
  // Deny administrative access to non-Administrators (Req 23.2).
  const decision = authorize(
    {
      authenticated: true,
      role: session.role,
      verificationStatus: session.verificationStatus,
    },
    "AdminPanel",
  );
  if (!decision.granted) {
    return (
      <div className="sb-main">
        <Card title="Access denied">
          <p className="sb-error" role="alert">
            You lack administrative privileges to view the Admin Panel.
          </p>
        </Card>
      </div>
    );
  }

  const isSuperAdmin = session.role === "SuperAdmin";

  return (
    <div className="sb-main">
      <h1>Admin Panel</h1>

      <AnalyticsSection />
      <VerificationQueue />
      <DonationModeration />
      <ReportReview />
      {isSuperAdmin && <AdminAccountManagement />}
    </div>
  );
}

function AnalyticsSection(): JSX.Element {
  return (
    <AsyncSection title="Analytics" loader={() => api.adminAnalytics()} emptyText="">
      {(a) => (
        <div className="sb-grid">
          <Stat label="Restaurants" value={a.restaurants} />
          <Stat label="NGOs" value={a.ngos} />
          <Stat label="Meals Donated" value={a.mealsDonated} />
          <Stat label="Meals Received" value={a.mealsReceived} />
          <Stat label="Completed Donations" value={a.completedDonations} />
          <Stat label="Expired Donations" value={a.expiredDonations} />
        </div>
      )}
    </AsyncSection>
  );
}

function VerificationQueue(): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  async function decide(
    uid: string,
    action: "approve" | "reject",
  ): Promise<void> {
    try {
      if (action === "approve") await api.approveUser({ targetUid: uid });
      else await api.rejectUser({ targetUid: uid });
      setResolved((prev) => new Set(prev).add(uid));
      setNotice(
        action === "approve" ? "Approval succeeded." : "Rejection succeeded.",
      );
    } catch {
      setNotice("Action failed. The status was left unchanged.");
    }
  }

  return (
    <AsyncSection
      title="Verification queue"
      loader={() => api.listPendingUsers()}
      emptyText="No pending verifications."
      isEmpty={(u: PendingUser[]) => u.length === 0}
    >
      {(users: PendingUser[]) => (
        <div>
          {notice && <p className="sb-muted" role="status">{notice}</p>}
          {users
            .filter((u) => !resolved.has(u.uid))
            .map((u) => (
              <div key={u.uid} className="sb-list-item">
                <div className="sb-row">
                  <strong>{u.email}</strong>
                  <span className="sb-pill">{u.role}</span>
                  <span className="sb-spacer" />
                  <button
                    type="button"
                    className="sb-btn"
                    onClick={() => void decide(u.uid, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="sb-btn sb-btn--danger"
                    onClick={() => void decide(u.uid, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </AsyncSection>
  );
}

function DonationModeration(): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);
  const [removed, setRemoved] = useState<Set<string>>(new Set());

  async function remove(donationId: string): Promise<void> {
    try {
      await api.deleteDonation({ donationId });
      setRemoved((prev) => new Set(prev).add(donationId));
      setNotice("Donation deleted.");
    } catch {
      setNotice("Deletion failed.");
    }
  }

  return (
    <AsyncSection
      title="Donation moderation"
      loader={() => api.listAllDonations()}
      emptyText="No donations to moderate."
      isEmpty={(ds: Donation[]) => ds.length === 0}
    >
      {(ds: Donation[]) => (
        <div>
          {notice && <p className="sb-muted" role="status">{notice}</p>}
          {ds
            .filter((d) => !removed.has(d.donationId))
            .map((d) => (
              <DonationRow
                key={d.donationId}
                donation={d}
                actions={
                  <button
                    type="button"
                    className="sb-btn sb-btn--danger"
                    onClick={() => void remove(d.donationId)}
                  >
                    Delete
                  </button>
                }
              />
            ))}
        </div>
      )}
    </AsyncSection>
  );
}

function ReportReview(): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  async function act(
    report: Report,
    action: "suspend" | "dismiss" | "remove",
  ): Promise<void> {
    try {
      await api.resolveReport({ reportId: report.reportId, action });
      setResolved((prev) => new Set(prev).add(report.reportId));
      setNotice("Report resolved.");
    } catch {
      setNotice("Action failed.");
    }
  }

  return (
    <AsyncSection
      title="Report review"
      loader={() => api.listPendingReports()}
      emptyText="No pending reports."
      isEmpty={(r: Report[]) => r.length === 0}
    >
      {(reports: Report[]) => (
        <div>
          {notice && <p className="sb-muted" role="status">{notice}</p>}
          {reports
            .filter((r) => !resolved.has(r.reportId))
            .map((r) => (
              <div key={r.reportId} className="sb-list-item">
                <div className="sb-row">
                  <strong>Reason: {r.reason}</strong>
                  <span className="sb-spacer" />
                  <span className="sb-muted">Target: {r.targetUid}</span>
                </div>
                <div className="sb-row" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="sb-btn sb-btn--secondary"
                    onClick={() => void act(r, "dismiss")}
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    className="sb-btn"
                    onClick={() => void act(r, "suspend")}
                  >
                    Suspend user
                  </button>
                  <button
                    type="button"
                    className="sb-btn sb-btn--danger"
                    onClick={() => void act(r, "remove")}
                  >
                    Remove user
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </AsyncSection>
  );
}

function AdminAccountManagement(): JSX.Element {
  const [uid, setUid] = useState("");
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setNotice(null);
    setBusy(true);
    try {
      await api.createAdminAccount({ uid, email });
      setNotice(`Admin account created for ${email}.`);
      setUid("");
      setEmail("");
    } catch {
      setNotice("Could not create the Admin account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Admin account management (Super Admin)">
      <form onSubmit={create}>
        <div className="sb-field">
          <label htmlFor="a-uid">Google UID</label>
          <input
            id="a-uid"
            className="sb-input"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
          />
        </div>
        <div className="sb-field">
          <label htmlFor="a-email">Email</label>
          <input
            id="a-email"
            className="sb-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {notice && <p className="sb-muted" role="status">{notice}</p>}
        <button type="submit" className="sb-btn" disabled={busy}>
          {busy ? "Creating..." : "Create Admin account"}
        </button>
      </form>
    </Card>
  );
}
